import { z } from "zod";

// --- Schemas ---

const AuditBoardSystemConfigSchema = z.object({
  baseUrl: z.string(),
  defaultWorkspaceId: z.string(),
});

export const SystemOfRecordSchema = z.object({
  id: z.string().uuid(),
  source: z.string(),
  auditboard: AuditBoardSystemConfigSchema.optional().nullable(),
});

export type SystemOfRecord = z.infer<typeof SystemOfRecordSchema>;

// --- Registry ---

const SYSTEM_REGISTRY: Record<
  string,
  { displayName: string; iconName: string }
> = {
  auditboard: { displayName: "Optro", iconName: "optro" },
  workiva: { displayName: "Workiva", iconName: "workiva" },
};

// --- Helpers ---

export function getSystemDisplayName(system: string): string {
  return SYSTEM_REGISTRY[system]?.displayName ?? system;
}

export function getSystemIconPath(system: string): string {
  const iconName = SYSTEM_REGISTRY[system]?.iconName ?? system;
  return `/apps/icons/${iconName}.svg`;
}

/**
 * Constructs the full external URL for a linked control given the
 * workspace's system-of-record config and the opaque compound key.
 * Returns `null` if the URL cannot be constructed.
 *
 * Compound key format: `source::controlId:workspaceId:controlInstanceId`
 */
export function getExternalControlUrl(
  systemOfRecord: SystemOfRecord,
  externalRef: string,
): string | null {
  const parts = externalRef.split("::");
  if (parts.length !== 2) return null;

  const [source, body] = parts;
  if (!body) return null;

  const [, , controlInstanceId] = body.split(":");

  switch (source) {
    case "auditboard": {
      const baseUrl = systemOfRecord.auditboard?.baseUrl;
      if (!baseUrl) return null;

      const workspaceId = body.split(":")[1];
      if (!workspaceId) return null;
      if (!controlInstanceId) return null;

      return `${baseUrl}/workspace/${workspaceId}/controls/control/${controlInstanceId}`;
    }
    default:
      return null;
  }
}

/**
 * Constructs the full external URL for a linked work step given the
 * workspace's system-of-record config and the ops-audit-item's numeric
 * ops_audit_item id. Returns `null` if the URL cannot be constructed.
 *
 * Unlike controls' compound-key ref, work steps carry the ops_audit_item id directly
 * on the structured externalRef object, and the ops-audit-item redirect
 * route has no workspace segment.
 */
export function getExternalWorkStepUrl(
  systemOfRecord: SystemOfRecord,
  opsAuditItemId: number | null | undefined,
): string | null {
  if (opsAuditItemId == null) return null;

  switch (systemOfRecord.source) {
    case "auditboard": {
      const baseUrl = systemOfRecord.auditboard?.baseUrl;
      if (!baseUrl) return null;

      return `${baseUrl}/sh/opsaudits/opsitem-redirect/${opsAuditItemId}`;
    }
    default:
      return null;
  }
}
